import React, { useRef, useEffect } from "react";

interface VideoSectionProps {
  supportsWebM: boolean;
  mainHeroVideo: string;
  mainHeroFallback: string;
  heroVideoRef: React.RefObject<HTMLVideoElement | null>;
  heroSectionRef: React.RefObject<HTMLDivElement | null>;
  setVideoDuration: (duration: number) => void;
  setIsVideoReady: (ready: boolean) => void;
  isIOS?: boolean;
  heroColorVideo?: string;
  heroAlphaVideo?: string;
  heroAlphaVideoRef?: React.RefObject<HTMLVideoElement | null>;
}

export const VideoSection: React.FC<VideoSectionProps> = ({
  supportsWebM,
  mainHeroVideo,
  mainHeroFallback,
  heroVideoRef,
  heroSectionRef,
  setVideoDuration,
  setIsVideoReady,
  isIOS = false,
  heroColorVideo,
  heroAlphaVideo,
  heroAlphaVideoRef,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const colorVideoRef = useRef<HTMLVideoElement>(null);
  const alphaVideoRef = useRef<HTMLVideoElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const syncCheckIntervalRef = useRef<number | null>(null);

  // iOS에서는 Canvas 합성 사용 안 함 (고정 이미지 사용)
  const useCanvasComposite = false; // isIOS && heroColorVideo && heroAlphaVideo;

  // Canvas 합성 로직
  useEffect(() => {
    if (!useCanvasComposite) return;

    const canvas = canvasRef.current;
    const vColor = colorVideoRef.current;
    const vAlpha = alphaVideoRef.current;

    if (!canvas || !vColor || !vAlpha) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    // 메타데이터 로드 시 duration 설정
    const handleMetadata = () => {
      if (vColor.duration) {
        setVideoDuration(vColor.duration);
        canvas.width = vColor.videoWidth;
        canvas.height = vColor.videoHeight;
        setIsVideoReady(true);
      }
    };

    vColor.addEventListener("loadedmetadata", handleMetadata);

    // 프레임 동기화 함수
    const syncVideos = () => {
      if (!vColor || !vAlpha) return;

      const timeDiff = Math.abs(vColor.currentTime - vAlpha.currentTime);
      const syncThreshold = 0.05; // 50ms 차이까지 허용

      if (timeDiff > syncThreshold) {
        // Alpha 비디오를 Color 비디오에 맞춤
        vAlpha.currentTime = vColor.currentTime;
      }
    };

    // Canvas 합성 루프
    const loop = () => {
      if (!vColor.paused && !vAlpha.paused && canvas.width > 0) {
        // 매 프레임마다 동기화 체크
        syncVideos();

        // Color 비디오를 캔버스에 그리기
        ctx.drawImage(vColor, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = img.data;

        // Alpha 비디오를 임시 캔버스로 가져오기
        const tmp = document.createElement("canvas");
        tmp.width = canvas.width;
        tmp.height = canvas.height;
        const tctx = tmp.getContext("2d", { willReadFrequently: true });
        if (tctx) {
          tctx.drawImage(vAlpha, 0, 0, canvas.width, canvas.height);
          const aimg = tctx.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
          ).data;

          // 그레이스케일 Alpha 채널을 RGB 이미지의 Alpha로 복사
          for (let i = 0, j = 0; i < data.length; i += 4, j += 4) {
            data[i + 3] = aimg[j]; // R 채널을 Alpha로 사용
          }
          ctx.putImageData(img, 0, 0);
        }
      }
      animationFrameRef.current = requestAnimationFrame(loop);
    };

    // 비디오 재생이 시작되면 루프 시작
    const handlePlay = () => {
      if (animationFrameRef.current === null) {
        loop();
      }
    };

    // 정기적인 동기화 체크 (추가 안전장치)
    const startSyncCheck = () => {
      if (syncCheckIntervalRef.current === null) {
        syncCheckIntervalRef.current = window.setInterval(() => {
          if (!vColor.paused && !vAlpha.paused) {
            syncVideos();
          }
        }, 100); // 100ms마다 체크
      }
    };

    const stopSyncCheck = () => {
      if (syncCheckIntervalRef.current !== null) {
        clearInterval(syncCheckIntervalRef.current);
        syncCheckIntervalRef.current = null;
      }
    };

    vColor.addEventListener("play", handlePlay);
    vColor.addEventListener("play", startSyncCheck);
    vColor.addEventListener("pause", stopSyncCheck);
    vColor.addEventListener("ended", stopSyncCheck);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      stopSyncCheck();
      vColor.removeEventListener("loadedmetadata", handleMetadata);
      vColor.removeEventListener("play", handlePlay);
      vColor.removeEventListener("play", startSyncCheck);
      vColor.removeEventListener("pause", stopSyncCheck);
      vColor.removeEventListener("ended", stopSyncCheck);
    };
  }, [useCanvasComposite, setVideoDuration, setIsVideoReady]);
  return (
    <section
      ref={heroSectionRef}
      className="relative flex items-center justify-center"
      style={{
        height: "min(100vw, 1280px)",
        width: "100vw",
        marginLeft: "calc(-50vw + 50%)",
        paddingLeft: 0,
        overflow: "hidden",
        backgroundColor: "#fcf7faff",
      }}
    >
      {useCanvasComposite ? (
        // iOS: Canvas 합성 방식 (color + alpha)
        <>
          <canvas
            ref={canvasRef}
            style={{
              width: "min(100vw, 1280px)",
              height: "min(100vw, 1280px)",
              objectFit: "cover",
              display: "block",
              marginLeft: 0,
              paddingLeft: 0,
            }}
          />
          {/* Hidden videos for canvas compositing */}
          <video
            ref={(el) => {
              colorVideoRef.current = el;
              if (heroVideoRef) {
                (
                  heroVideoRef as React.MutableRefObject<HTMLVideoElement | null>
                ).current = el;
              }
            }}
            muted
            playsInline
            preload="auto"
            style={{ display: "none" }}
          >
            <source src={heroColorVideo} type="video/mp4" />
          </video>
          <video
            ref={(el) => {
              alphaVideoRef.current = el;
              if (heroAlphaVideoRef) {
                (
                  heroAlphaVideoRef as React.MutableRefObject<HTMLVideoElement | null>
                ).current = el;
              }
            }}
            muted
            playsInline
            preload="auto"
            style={{ display: "none" }}
          >
            <source src={heroAlphaVideo} type="video/mp4" />
          </video>
        </>
      ) : supportsWebM ? (
        <video
          ref={heroVideoRef}
          muted
          playsInline
          preload="auto"
          poster={mainHeroFallback}
          onLoadedMetadata={() => {
            const video = heroVideoRef.current;
            if (video && video.duration) {
              setVideoDuration(video.duration);
              video.currentTime = 0;
              setIsVideoReady(true);
            }
          }}
          onCanPlay={() => {
            // 비디오가 재생 가능한 상태가 되면 준비 완료 표시
            setIsVideoReady(true);
          }}
          onError={(e) => {
            const video = e.currentTarget;
            video.style.display = "none";
            const fallbackImg = document.createElement("img");
            fallbackImg.src = mainHeroFallback;
            fallbackImg.alt = "Chart Dashboard";
            fallbackImg.style.cssText = video.style.cssText;
            video.parentElement?.appendChild(fallbackImg);
          }}
          style={{
            width: "min(100vw, 1280px)",
            height: "min(100vw, 1280px)",
            objectFit: "cover",
            display: "block",
            marginLeft: 0,
            paddingLeft: 0,
          }}
        >
          <source src={mainHeroVideo} type="video/webm" />
          <img
            src={mainHeroFallback}
            alt="Chart Dashboard Fallback"
            style={{
              width: "min(100vw, 1280px)",
              height: "min(100vw, 1280px)",
              objectFit: "cover",
              display: "block",
              marginLeft: 0,
              paddingLeft: 0,
            }}
          />
        </video>
      ) : (
        <img
          src={mainHeroFallback}
          alt="Chart Dashboard"
          style={{
            width: "min(100vw, 1280px)",
            height: "min(100vw, 1280px)",
            objectFit: "cover",
            display: "block",
            marginLeft: 0,
            paddingLeft: 0,
          }}
        />
      )}
    </section>
  );
};
